import { useState, useEffect, useRef } from 'react';

const TYPING_SPEED_MS = 100;

const useChatStreaming = ({ typingSpeedMs = TYPING_SPEED_MS, onStreamComplete }) => {
    const [streamingState, setStreamingState] = useState(null);
    const onStreamCompleteRef = useRef(onStreamComplete);
    const pendingStreamsRef = useRef([]);
    const isStreamingRef = useRef(false);

    useEffect(() => { onStreamCompleteRef.current = onStreamComplete; });

    useEffect(() => {
        if (!streamingState) return;

        const words = streamingState.fullText.split(' ');
        let currentWordIndex = 0;

        const intervalId = setInterval(() => {
            if (currentWordIndex < words.length) {
                const newDisplayText = words.slice(0, currentWordIndex + 1).join(' ');
                setStreamingState(prev => ({ ...prev, displayText: newDisplayText }));
                currentWordIndex++;
            } else {
                clearInterval(intervalId);
                const finalMessage = {
                    id: streamingState.id,
                    type: 'text',
                    text: streamingState.fullText,
                    user: 'bot',
                    timestamp: new Date().toISOString(),
                    endConversation: streamingState.endConversation === true,
                };
                console.log('[Stream] ✅ complete, length=', streamingState.fullText?.length);
                const isLast = pendingStreamsRef.current.length === 0;
                onStreamCompleteRef.current?.(finalMessage, { isLast });
                if (!isLast) {
                    const next = pendingStreamsRef.current.shift();
                    setStreamingState({
                        fullText: next.text,
                        displayText: '',
                        id: crypto.randomUUID?.() ?? Math.random().toString(36).slice(2),
                        endConversation: next.endConversation === true,
                    });
                } else {
                    isStreamingRef.current = false;
                    setStreamingState(null);
                }
            }
        }, typingSpeedMs);

        return () => clearInterval(intervalId);
    }, [streamingState?.id]);

    const startStream = ({ text, endConversation = false }) => {
        console.log('[Stream] 🎬 start, length=', text?.length);
        if (isStreamingRef.current) {
            pendingStreamsRef.current.push({ text, endConversation });
            return;
        }
        isStreamingRef.current = true;
        setStreamingState({
            fullText: text,
            displayText: '',
            id: crypto.randomUUID?.() ?? Math.random().toString(36).slice(2),
            endConversation,
        });
    };

    const cancelStream = () => {
        pendingStreamsRef.current = [];
        isStreamingRef.current = false;
        setStreamingState(null);
    };

    return { streamingState, startStream, cancelStream };
};

export default useChatStreaming;
